import { ControlledModal } from 'components/ControlledModal';
import { useState } from 'react';
import { useGetOrganizations } from 'api/useOrganizationsApi';
import { OrganizationResponse } from 'api/core';
import { buildQueryState } from 'components/Table/useTableQueryState';

interface OrganizationHierarchyProps {
  trigger?: React.ReactNode;
  isInitialOpen?: boolean;
  onClosed?: () => void;
}

const getMermaidCode = (organizations: OrganizationResponse[]) => {
  const cleanId = (id: string) => id.replace(/[^a-zA-Z0-9_]/g, '_');

  let mermaidCode = 'graph LR;\n';

  const orgToName = new Map<string, string>();
  organizations.forEach((org) => {
    orgToName.set(org.id, org.name);
  });

  // Order organizations by their parent name (if any), then by their own name
  const orderedOrganizations = organizations.sort((a, b) => {
    const parentA = a.parentOrganizationId
      ? orgToName.get(a.parentOrganizationId) || ''
      : '';
    const parentB = b.parentOrganizationId
      ? orgToName.get(b.parentOrganizationId) || ''
      : '';
    return parentA.localeCompare(parentB) || a.name.localeCompare(b.name);
  });

  mermaidCode += '\n%% Companies\n';
  orderedOrganizations.forEach((org) => {
    mermaidCode += `${cleanId(org.id)}["${org.name}"];\n`;
  });

  mermaidCode += '\n%% Hierarchy\n';
  orderedOrganizations.forEach((org) => {
    if (org.parentOrganizationId) {
      const parentId = cleanId(org.parentOrganizationId);
      const childId = cleanId(org.id);
      mermaidCode += `${parentId} --> ${childId};\n`;
    }
  });

  return mermaidCode;
};

export const OrganizationHierarchy = ({
  trigger,
  isInitialOpen,
  onClosed,
}: OrganizationHierarchyProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(isInitialOpen ?? false);

  const { data: organizations } = useGetOrganizations(
    buildQueryState<OrganizationResponse>({
      pagination: {
        pageIndex: 0,
        pageSize: 100,
      },
      sorting: [
        {
          id: 'name',
          desc: false,
        },
      ],
    })
  );

  if (!organizations) return null;

  const onModalStateChange = (state: boolean) => {
    setIsOpen(state);
    if (!state) {
      onClosed?.();
    }
  };

  return (
    <>
      {trigger ? (
        <div onClick={() => onModalStateChange(true)}>{trigger}</div>
      ) : null}
      <ControlledModal
        showModal={onModalStateChange}
        isOpen={isOpen}
        title="Organisationens hierarki"
        closeOnOutsideClick
        hideActionBar
      >
        <div className="mockup-code text-primary">
          <pre className="px-6 select-all">
            <code>{getMermaidCode(organizations.data)} </code>
          </pre>
        </div>
      </ControlledModal>
    </>
  );
};
